import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { DateRangePicker } from '@shared/components/DateRangePicker';
import { View } from '@shared/components/View';
import { DateField } from '@shared/data/entity/fields/DateField';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { FormattedDateRange } from '@shared/scenes/cards/collectionView/components/fields/DateRangeCollectionViewField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { UpdateField } from '@shared/scenes/entity/types';
import { getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import styles from './DateRangeEditField.module.css';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: DateRangeField | null;
  startDateField: DateField | null;
  endDateField: DateField | null;
  updateField: UpdateField;
  component: API.DateRangeComponent;
  error?: string;
  style?: CSSProperties;
}

export function DateRangeEditField({
  field,
  startDateField,
  endDateField,
  updateField,
  component,
  error,
  style,
}: Props) {
  const componentId = component.id;
  const anchorName = `--date-range-edit-${component.id}`;
  const { startDateComponentId, endDateComponentId } = component;
  const isReadOnly = 'readonly' in component && !!component.readonly;

  const [isPickerOpen, openPicker, closePicker] = useBoolean(false);
  const [startDate, setStartDate] = useState(startDateField?.date);
  const [endDate, setEndDate] = useState(endDateField?.date);

  const isResponsiveForms = useResponsiveFormContext();
  const showError = !isEmpty(error);
  const hasValue = !isNil(field) && !field.isEmpty;
  const showClearButton = hasValue && !isReadOnly;
  const placeholder = getComponentPlaceholder(component);

  const onClear = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
  }, []);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setStartDate(endDate);
      setEndDate(startDate);
      // return early, this effect will re-run with start/end in the correct
      // order, ensuring the date range is valid
      return;
    }
    updateField(componentId, DateRangeField.fromFieldJson({ start: startDate, end: endDate }));
    updateField(startDateComponentId, DateField.fromFieldJson(startDate));
    updateField(endDateComponentId, DateField.fromFieldJson(endDate));
  }, [startDate, endDate, updateField, componentId, startDateComponentId, endDateComponentId]);

  return (
    <EditFieldContainer
      component={component}
      showClearButton={showClearButton}
      showLabel={isResponsiveForms || hasValue}
      showPlaceholder={false}
      onClear={onClear}
      error={showError ? error : undefined}
      anchorName={anchorName}
      onPress={isReadOnly ? undefined : openPicker}
      includeWrapperPadding={false}
      style={style}
    >
      <DateRangeContent
        isReadOnly={isReadOnly}
        isResponsiveForms={isResponsiveForms}
        field={field}
        placeholder={placeholder}
      />
      {isPickerOpen && (
        <AnchoredSheet anchorName={anchorName} onClose={closePicker} initialSize="full">
          <View padding={1}>
            <DateRangePicker
              startDateStamp={startDate}
              endDateStamp={endDate}
              setStartDateStamp={setStartDate}
              setEndDateStamp={setEndDate}
            />
          </View>
        </AnchoredSheet>
      )}
    </EditFieldContainer>
  );
}

interface DateRangeContentProps {
  isReadOnly: boolean;
  isResponsiveForms: boolean;
  field: DateRangeField | null;
  placeholder: string | undefined;
}

function DateRangeContent({
  isReadOnly,
  isResponsiveForms,
  field,
  placeholder,
}: DateRangeContentProps) {
  const hasValue = !isNil(field) && !field.isEmpty;

  return (
    <View
      className={[
        styles.fieldContent,
        isReadOnly && styles.readonly,
        isResponsiveForms && styles.responsiveFieldContent,
      ]}
    >
      {hasValue && <FormattedDateRange field={field} />}
      {!hasValue && <View className={styles.placeholder}>{placeholder}</View>}
      <View />
    </View>
  );
}
